import { useEffect, useState } from "react";
import { FONTS } from "../../assets/fonts";

/** Default 16-swatch brand palette shown beneath every ColorRow. */
export const PALETTE = [
  "#0c2572", "#1e40af", "#0ea5e9", "#06b6d4",
  "#16a34a", "#65a30d", "#facc15", "#f97316",
  "#dc2626", "#ec4899", "#8b5cf6", "#6366f1",
  "#0f172a", "#475569", "#94a3b8", "#ffffff",
];

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export function Slider({
  label, min, max, step, value, onChange, onCommit,
}: {
  label: string; min: number; max: number; step: number; value: number;
  onChange: (v: number) => void; onCommit?: () => void;
}) {
  return (
    <label className="field">
      <span>{label} <strong>{value}</strong></span>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        onMouseUp={() => onCommit?.()}
        onTouchEnd={() => onCommit?.()}
      />
    </label>
  );
}

export function ColorRow({
  label,
  color,
  onChange,
  allowTransparent = false,
}: {
  label: string;
  color: string;
  onChange: (c: string) => void;
  allowTransparent?: boolean;
}) {
  const isTransparent = color === "transparent" || color === "";
  const [hexDraft, setHexDraft] = useState(isTransparent ? "transparent" : color);

  useEffect(() => {
    setHexDraft(color === "transparent" || color === "" ? "transparent" : color);
  }, [color]);

  function commitHex() {
    const raw = hexDraft.trim().toLowerCase();
    if (allowTransparent && (raw === "transparent" || raw === "" || raw === "none")) {
      onChange("transparent");
      return;
    }
    const withHash = raw.startsWith("#") ? raw : `#${raw}`;
    if (/^#[0-9a-f]{6}$/i.test(withHash)) {
      onChange(withHash);
    } else if (/^#[0-9a-f]{3}$/i.test(withHash)) {
      const m = withHash.slice(1);
      onChange(`#${m[0]}${m[0]}${m[1]}${m[1]}${m[2]}${m[2]}`);
    } else {
      setHexDraft(isTransparent ? "transparent" : color);
    }
  }

  return (
    <div className="color-row">
      <div className="color-row-head">
        <span>{label}</span>
        <div className="color-row-controls">
          <input
            type="text"
            className="hex-input"
            value={hexDraft}
            onChange={(e) => setHexDraft(e.target.value)}
            onBlur={commitHex}
            onKeyDown={(e) => {
              if (e.key === "Enter") { commitHex(); (e.target as HTMLInputElement).blur(); }
              if (e.key === "Escape") {
                setHexDraft(isTransparent ? "transparent" : color);
                (e.target as HTMLInputElement).blur();
              }
            }}
            spellCheck={false}
            maxLength={11}
            placeholder={allowTransparent ? "#000000 / transparent" : "#000000"}
            aria-label={`${label} hex`}
          />
          <input
            type="color"
            value={isTransparent ? "#ffffff" : color}
            onChange={(e) => onChange(e.target.value)}
            aria-label={`${label} color picker`}
          />
        </div>
      </div>
      <div className="swatches">
        {allowTransparent && (
          <button
            type="button"
            className={`swatch swatch-transparent ${isTransparent ? "active" : ""}`}
            onClick={() => onChange("transparent")}
            title="Transparent (PNG export will be transparent)"
          >
            <span>∅</span>
          </button>
        )}
        {PALETTE.map((c) => (
          <button
            key={c}
            className={`swatch ${!isTransparent && color.toLowerCase() === c.toLowerCase() ? "active" : ""}`}
            style={{ background: c }}
            onClick={() => onChange(c)}
            title={c}
          />
        ))}
      </div>
    </div>
  );
}

export function FontPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {FONTS.map((f) => (
        <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>
          {f.family} ({f.category})
        </option>
      ))}
    </select>
  );
}

/** Slugify text for filenames. */
export function slug(s: string) {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "logo";
}

/** Map an aesthetic-score grade letter to a CSS class suffix. */
export function gradeClass(g: string): string {
  const c = g[0]?.toLowerCase() ?? "c";
  return c === "a" ? "a" : c === "b" ? "b" : c === "c" ? "c" : "d";
}
